import { useEffect, useRef, useState } from 'react';
import { Sparkles, Award, Download, Upload, Trash2, Loader2 } from 'lucide-react';
import { apiService } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { useSnapshotDraft, getDraftPayload, draftKey } from '@/stores/unsavedDrafts.store';
import styles from '@/pages/GradesPage.module.scss';

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

/** Vignette d'un cosmetic + actions (télécharger / remplacer / supprimer).
 * Le remplacement de texture est mis en attente (brouillon, barre du bas) ; la suppression est immédiate. */
function Thumb({
  containerId, project, type, name, onChanged,
}: {
  containerId: string;
  project: string;
  type: 'nametag' | 'ranktag';
  name: string;
  onChanged: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [bust, setBust] = useState(0);
  const [pending, setPending] = useState<string | null>(() => {
    const p = getDraftPayload(draftKey('cosmetic-texture', containerId, `${type}:${name}`));
    return typeof p === 'string' ? p : null;
  });
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!containerId || !name) { setUrl(null); return; }
    let active = true;
    let created: string | null = null;
    apiService.getCosmeticBlobUrl(containerId, type, name, bust || undefined).then((r) => {
      if (!active) { if (r) URL.revokeObjectURL(r); return; }
      created = r;
      setUrl(r);
    });
    return () => { active = false; if (created) URL.revokeObjectURL(created); };
  }, [containerId, type, name, bust]);

  const onPick = async (f: File) => {
    if (!f.type.includes('png')) return;
    setPending(await readAsDataUrl(f)); // mis en attente, écrit à l'« Enregistrer tout »
    if (fileRef.current) fileRef.current.value = '';
  };

  const save = async () => {
    if (!pending) return true;
    const res = await apiService.replaceCosmeticTexture(containerId, type, name, pending);
    const ok = !!res.data?.success;
    if (ok) { setPending(null); setBust(Date.now()); }
    return ok;
  };

  useSnapshotDraft({
    kind: 'cosmetic-texture',
    groupId: containerId,
    identifier: `${type}:${name}`,
    label: `Texture · ${name}`,
    dirty: !!pending,
    payload: pending,
    save,
  });

  const onDelete = async () => {
    const label = type === 'ranktag' ? 'rank tag' : 'ornement';
    if (!window.confirm(`Supprimer le ${label} « ${name} » ? Il sera retiré de tous les containers et son PNG supprimé (à publier via Déploiement).`)) return;
    setDeleting(true);
    try {
      const res = await apiService.deleteCosmetic(containerId, project, type, name);
      if (res.data?.success) onChanged();
    } finally {
      setDeleting(false);
    }
  };

  const preview = pending ?? url;

  return (
    <div className={styles.cosmeticCard}>
      <div className={styles.cosmeticThumb}>
        {preview ? <img src={preview} alt={name} /> : <span className={styles.cosmeticMissing}>—</span>}
        {pending && <span className={styles.cosmeticPending}>modifié</span>}
      </div>
      <span className={styles.cosmeticName}>{name}</span>
      <div className={styles.cosmeticActions}>
        {url && (
          <a className={styles.cosmeticAction} href={url} download={`${name}.png`} title="Télécharger">
            <Download size={14} />
          </a>
        )}
        <button className={styles.cosmeticAction} disabled={deleting} onClick={() => fileRef.current?.click()} title="Remplacer la texture (mis en attente)">
          <Upload size={14} />
        </button>
        <button className={`${styles.cosmeticAction} ${styles.cosmeticActionDanger}`} disabled={deleting} onClick={onDelete} title="Supprimer">
          {deleting ? <Loader2 size={14} className={styles.spin} /> : <Trash2 size={14} />}
        </button>
        <input ref={fileRef} type="file" accept="image/png" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); }} />
      </div>
    </div>
  );
}

/** Listing des ornements (nametags) et rank tags existants du pack, avec aperçu + actions.
 * `type` filtre l'affichage à un seul type (utilisé sous l'onglet correspondant). */
export function CosmeticList({ refreshKey, type }: { refreshKey?: number; type?: 'nametag' | 'ranktag' }) {
  const project = useGameStore((s) => s.activeGameId);
  const [containerId, setContainerId] = useState('');
  const [nametags, setNametags] = useState<string[]>([]);
  const [ranktags, setRanktags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [localReload, setLocalReload] = useState(0);

  useEffect(() => {
    apiService.getContainers().then((r) => {
      const list = (r.data?.containers ?? []).filter((c) => (c.project ?? 'paladium') === project);
      setContainerId(list[0]?.id ?? '');
    });
  }, [project]);

  useEffect(() => {
    if (!containerId) { setNametags([]); setRanktags([]); return; }
    let active = true;
    setLoading(true);
    apiService.getContainerPremium(containerId).then((r) => {
      if (!active) return;
      setNametags(r.data?.nametags ?? []);
      setRanktags(r.data?.ranktags ?? []);
      setLoading(false);
    });
    return () => { active = false; };
  }, [containerId, refreshKey, localReload]);

  if (!containerId) return <p className={styles.muted}>Aucun container pour ce projet.</p>;
  if (loading) return <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement…</span></div>;

  const showNametags = !type || type === 'nametag';
  const showRanktags = !type || type === 'ranktag';
  const bump = () => setLocalReload((k) => k + 1);

  return (
    <>
      {showNametags && (
        <>
          <div className={styles.sectionLabel}><Sparkles size={14} /> Ornements existants ({nametags.length})</div>
          {nametags.length === 0 ? (
            <p className={styles.muted}>Aucun ornement.</p>
          ) : (
            <div className={styles.cosmeticGrid}>
              {nametags.map((n) => <Thumb key={n} containerId={containerId} project={project} type="nametag" name={n} onChanged={bump} />)}
            </div>
          )}
        </>
      )}

      {showRanktags && (
        <>
          <div className={styles.sectionLabel}><Award size={14} /> Rank tags existants ({ranktags.length})</div>
          {ranktags.length === 0 ? (
            <p className={styles.muted}>Aucun rank tag.</p>
          ) : (
            <div className={styles.cosmeticGrid}>
              {ranktags.map((n) => <Thumb key={n} containerId={containerId} project={project} type="ranktag" name={n} onChanged={bump} />)}
            </div>
          )}
        </>
      )}
    </>
  );
}
